import React, { useState, useEffect, useCallback } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField as MuiTextField, Tooltip, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
  TransferenciaResumoResponse,
  TransferenciaResumoFilter,
  TransferenciaStatusEnum,
} from 'common/genericTypes';
import {
  transferenciaStatusDescriptions,
  transferenciaTipoDescriptions,
} from 'common/genericDescriptions';
import { MoreOptionsMenu, SmSpace, MdSpace } from 'm2m.components';
import { Button } from 'm2m.components';
import { Cancel, CheckCircle, Description, Error, WatchLater, ThumbUp, ThumbDown } from '@material-ui/icons';
import AttachFile from '@material-ui/icons/AttachFile';
import { useGenericStyles } from 'common/genericStyles';
import { RootState } from 'store';
import { formatDate } from 'utils/date';
import { addCpfCnpjMask } from 'utils/mask';
import { formatNumber } from 'utils/number';
import { useHistory } from 'react-router-dom';
import { componentsPropsValues, localizedTextsMap, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import {
  DataGridPro,
  GridCellParams,
  GridValueGetterParams,
  GridSortModel,
  GridFilterModel,
  GridSelectionModel,
  getGridStringOperators,
  getGridDateOperators,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid-pro';
import { GestaoDocumentosTransferenciaDrawer } from './gestaoDocumentosTransferenciaDrawer';
import { AprovacaoEmLoteTransferenciasModal } from './aprovacaoEmLoteTransferenciasModal';
import { GETTransferenciaDocumentos, POSTAprovacaoTransferenciaEmLote, POSTReprovacaoTransferenciaEmLote } from 'requests/operacao';
import { CompraVendaDocumento } from 'common/genericTypes';
import { addAlert } from 'store/modules/snackBar';

const getStatusIcon = (status: string | null, classes: any) => {
  switch (status) {
    case TransferenciaStatusEnum.Concluido:
      return <CheckCircle fontSize="small" style={{ color: '#00c853', fontSize: 24 }} />;
    case TransferenciaStatusEnum.Reprovado:
      return <Cancel fontSize="small" style={{ color: '#f44336', fontSize: 24 }} />;
    case TransferenciaStatusEnum.Pendente:
      return <Error fontSize="small" style={{ color: '#2962ff', fontSize: 24 }} />;
    default:
      return <WatchLater fontSize="small" style={{ color: '#9e9e9e', fontSize: 24 }} />;
  }
};

const getStatusLabel = (status: string | null | undefined): string => {
  if (status === null || status === undefined) return 'Indefinido';
  const label = transferenciaStatusDescriptions[status];
  if (label) return label;
  // Fallback: formatar o valor bruto removendo underscores
  return status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
};

const Transferencias: React.FC = () => {
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [pageCount, setPageCount] = useState<number>(0);
  const [operacoes, setOperacoes] = useState<Array<TransferenciaResumoResponse>>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [linkOperator, setLinkOperator] = useState<any>('AND');
  const [transferenciaFilter, setTransferenciaFilter] = useState<Array<TransferenciaResumoFilter>>([]);

  // Seleção para aprovação em lote
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [openAprovacaoEmLoteModal, setOpenAprovacaoEmLoteModal] = useState<boolean>(false);

  // Gestão de comprovantes
  const [openGestaoComprovantesDrawer, setOpenGestaoComprovantesDrawer] = useState<boolean>(false);
  const [comprovantesTransferenciaId, setComprovantesTransferenciaId] = useState<string | undefined>(undefined);
  const [comprovantesCodigoIF, setComprovantesCodigoIF] = useState<string | undefined>(undefined);
  const [transferenciaDocumentos, setTransferenciaDocumentos] = useState<Array<CompraVendaDocumento>>([]);

  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useGenericStyles();

  const participanteLogado = useSelector((state: RootState) => state.auth.usuario.participante);
  const isDepositaria = participanteLogado.funcao === 'DEPOSITARIA';

  const { getTransferenciasResumoPaginated } = useSelector(
    (state: RootState) => state.operacaoApi
  );

  const getOperacoes = useCallback(async () => {
    try {
      setShowLoader(true);
      const result = await getTransferenciasResumoPaginated(
        dispatch,
        { page: page + 1, pageSize, linkOperator: linkOperator, transferenciaResumoFilter: transferenciaFilter },
        true
      );
      setOperacoes(result.result);
      setPageCount(result.totalCount);
      setShowLoader(false);
    } catch {
      setShowLoader(false);
    }
  }, [dispatch, getTransferenciasResumoPaginated, transferenciaFilter, page, pageSize, linkOperator]);

  const onFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!filterModel.items) {
        setTransferenciaFilter([]);
        return;
      }
      setLinkOperator(filterModel?.linkOperator?.toString()?.toUpperCase() ?? 'AND');
      var newFilter = new Array<TransferenciaResumoFilter>();

      filterModel.items.forEach((filter) => {
        let operatorValue =
          filter.operatorValue === 'is'
            ? 'EQUALS'
            : filter.operatorValue === 'not'
            ? 'NOT_EQUALS'
            : filter.operatorValue == 'startsWith'
            ? 'STARTS_WITH'
            : filter.operatorValue == 'endsWith'
            ? 'ENDS_WITH'
            : filter.operatorValue?.toUpperCase();

        let formattedValue = filter.value?.toString();
        if (formattedValue) {
          if (filter.columnField == 'DATA_CRIACAO') {
            formattedValue = formattedValue.split('-');
            formattedValue = `${formattedValue[2]}/${formattedValue[1]}/${formattedValue[0]}`;
          }

          let params = {
            columnType: filter.columnField.toUpperCase(),
            operationFilterType: operatorValue,
            value: formattedValue,
          } as TransferenciaResumoFilter;

          newFilter.push(params);
        }
        else {
          if (operatorValue == 'ISNOTEMPTY' || operatorValue == 'ISEMPTY') {
            newFilter.push({
              columnType: filter.columnField.toUpperCase(),
              operationFilterType: operatorValue,
              value: '',
            } as TransferenciaResumoFilter);
          }
        }
      });

      setTransferenciaFilter(newFilter);
      setPage(0);
    },
    []
  );

  const onSortChange = useCallback(
    (sortModel: GridSortModel) => {
      var newFilter = transferenciaFilter.filter(
        (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC'
      ) as Array<TransferenciaResumoFilter>;

      if (sortModel == null || sortModel.length === 0) {
        setTransferenciaFilter([...newFilter]);
        return;
      }

      var sort = sortModel[0];
      let params = {
        columnType: sort.field.toUpperCase(),
        orderByType: sort.sort?.toUpperCase(),
      } as TransferenciaResumoFilter;

      newFilter.push(params);
      setTransferenciaFilter([...newFilter]);
    },
    [transferenciaFilter]
  );

  useEffect(() => {
    getOperacoes();
  }, [getOperacoes, page, transferenciaFilter]);

  // --- Aprovação em lote ---
  // Apenas transferências com status PENDENTE (custódia ou titularidade)
  const operacoesSelecionadasEmAnalise = operacoes.filter(
    (op) =>
      selectionModel.includes(op.id) &&
      op.status === TransferenciaStatusEnum.Pendente
  );

  const handleOpenAprovacaoEmLote = () => {
    setOpenAprovacaoEmLoteModal(true);
  };

  const handleCloseAprovacaoEmLote = () => {
    setOpenAprovacaoEmLoteModal(false);
  };

  const handleAprovacaoEmLoteSuccess = async () => {
    setSelectionModel([]);
    await getOperacoes();
  };

  // --- Gestão de comprovantes ---
  const handleOpenGestaoComprovantes = async (item: TransferenciaResumoResponse) => {
    try {
      const docs = await GETTransferenciaDocumentos(dispatch, item.id, true);
      setTransferenciaDocumentos(docs);
    } catch {
      setTransferenciaDocumentos([]);
    }
    setComprovantesTransferenciaId(item.id);
    setComprovantesCodigoIF(item.instrumentoFinanceiroCodigo);
    setOpenGestaoComprovantesDrawer(true);
  };

  const handleCloseGestaoComprovantes = () => {
    setOpenGestaoComprovantesDrawer(false);
    setComprovantesTransferenciaId(undefined);
    setComprovantesCodigoIF(undefined);
    setTransferenciaDocumentos([]);
  };

  const handleGestaoComprovantesUploadSuccess = async () => {
    if (comprovantesTransferenciaId) {
      try {
        const docs = await GETTransferenciaDocumentos(dispatch, comprovantesTransferenciaId, true);
        setTransferenciaDocumentos(docs);
      } catch {
        setTransferenciaDocumentos([]);
      }
    }
    await getOperacoes();
  };

  // --- Aprovação / Reprovação individual ---
  const [openReprovacaoDialog, setOpenReprovacaoDialog] = useState<boolean>(false);
  const [reprovacaoOperacaoId, setReprovacaoOperacaoId] = useState<string | undefined>(undefined);
  const [reprovacaoJustificativa, setReprovacaoJustificativa] = useState<string>('');

  const handleAprovarIndividual = async (operacao: TransferenciaResumoResponse) => {
    try {
      const resultado = await POSTAprovacaoTransferenciaEmLote(dispatch, [operacao.id], true);
      if (resultado.itens?.some((i) => !i.sucesso)) {
        const erroMsg = resultado.itens.find((i) => !i.sucesso)?.mensagemErro ?? 'Falha ao aprovar transferência.';
        dispatch(addAlert({ severity: 'error', message: erroMsg }));
      } else {
        dispatch(addAlert({ severity: 'success', message: 'Transferência aprovada com sucesso.' }));
      }
      await getOperacoes();
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData?.itens?.length > 0) {
        const erroMsg = responseData.itens.find((i: any) => !i.sucesso)?.mensagemErro ?? responseData.mensagem ?? 'Erro ao aprovar transferência.';
        dispatch(addAlert({ severity: 'error', message: erroMsg }));
      } else {
        const msg = responseData?.mensagem ?? responseData?.message ?? 'Erro ao aprovar transferência.';
        dispatch(addAlert({ severity: 'error', message: msg }));
      }
    }
  };

  const handleOpenReprovacao = (operacao: TransferenciaResumoResponse) => {
    setReprovacaoOperacaoId(operacao.id);
    setReprovacaoJustificativa('');
    setOpenReprovacaoDialog(true);
  };

  const handleCloseReprovacao = () => {
    setOpenReprovacaoDialog(false);
    setReprovacaoOperacaoId(undefined);
    setReprovacaoJustificativa('');
  };

  const handleConfirmarReprovacao = async () => {
    if (!reprovacaoOperacaoId || !reprovacaoJustificativa.trim()) return;
    try {
      const resultado = await POSTReprovacaoTransferenciaEmLote(dispatch, [reprovacaoOperacaoId], reprovacaoJustificativa, true);
      if (resultado.itens?.some((i) => !i.sucesso)) {
        const erroMsg = resultado.itens.find((i: any) => !i.sucesso)?.mensagemErro ?? 'Falha ao reprovar transferência.';
        dispatch(addAlert({ severity: 'error', message: erroMsg }));
      } else {
        dispatch(addAlert({ severity: 'success', message: 'Transferência reprovada com sucesso.' }));
      }
      handleCloseReprovacao();
      await getOperacoes();
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData?.itens?.length > 0) {
        const erroMsg = responseData.itens.find((i: any) => !i.sucesso)?.mensagemErro ?? responseData.mensagem ?? 'Erro ao reprovar transferência.';
        dispatch(addAlert({ severity: 'error', message: erroMsg }));
      } else {
        const msg = responseData?.mensagem ?? responseData?.message ?? 'Erro ao reprovar transferência.';
        dispatch(addAlert({ severity: 'error', message: msg }));
      }
    }
  };

  // --- Colunas do grid ---
  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains', 'startsWith', 'endsWith'].includes(value)
  );

  const filterOperatorsData = getGridDateOperators();

  const filterOperatorsSelect = getGridSingleSelectOperators().filter(({ value }) =>
    ['is', 'not'].includes(value)
  );

  const statusValueOptions = [
    { value: 'PENDENTE', label: 'Em Análise' },
    { value: 'CONCLUIDO', label: 'Concluído' },
    { value: 'REPROVADO', label: 'Reprovado' },
  ];

  const getGridColumns = useCallback(
    () => [
      {
        field: 'STATUS',
        headerName: 'Status',
        type: 'singleSelect',
        valueOptions: statusValueOptions,
        filterOperators: filterOperatorsSelect,
        filterable: true,
        sortable: true,
        width: 80,
        minWidth: 60,
        maxWidth: 100,
        valueGetter: (params: GridValueGetterParams): string => (params.row as TransferenciaResumoResponse).status ?? '',
        renderCell: (params: GridCellParams) => {
          const row = params.row as TransferenciaResumoResponse;
          return (
            <Tooltip title={getStatusLabel(row.status)}>
              <span style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                {getStatusIcon(row.status, classes)}
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: 'DATA_CRIACAO',
        headerName: 'Data de Criação',
        type: 'date',
        filterOperators: filterOperatorsData,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const row = params.row as TransferenciaResumoResponse;
          return formatDate(new Date(row.dataDeCriacao));
        },
        width: 140,
        minWidth: 130,
        maxWidth: 200,
      },
      {
        field: 'CODIGO',
        headerName: 'Código',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as TransferenciaResumoResponse).instrumentoFinanceiroCodigo,
        width: 160,
        minWidth: 140,
        maxWidth: 250,
      },
      {
        field: 'TIPO',
        headerName: 'Tipo',
        type: 'singleSelect',
        valueOptions: [
          { value: 'TITULARIDADE', label: 'Titularidade' },
          { value: 'CUSTODIA', label: 'Custódia' },
        ],
        filterOperators: filterOperatorsSelect,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams): string => {
          const row = params.row as TransferenciaResumoResponse;
          return transferenciaTipoDescriptions[row.transferenciaTipo] ?? row.transferenciaTipo;
        },
        width: 170,
        minWidth: 140,
        maxWidth: 250,
      },
      {
        field: 'CUSTODIANTE_ORIGEM',
        headerName: 'Custodiante Origem',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as TransferenciaResumoResponse).custodianteOrigemRazaoSocial,
        width: 220,
        minWidth: 150,
        maxWidth: 350,
      },
      {
        field: 'INVESTIDOR_ORIGEM',
        headerName: 'Investidor Origem',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const cpfCnpj = (params.row as TransferenciaResumoResponse).investidorOrigemCpfCnpj;
          return cpfCnpj ? addCpfCnpjMask(cpfCnpj) : '';
        },
        width: 180,
        minWidth: 150,
        maxWidth: 250,
      },
      {
        field: 'CUSTODIANTE_DESTINO',
        headerName: 'Custodiante Destino',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as TransferenciaResumoResponse).custodianteDestinoRazaoSocial,
        width: 220,
        minWidth: 150,
        maxWidth: 350,
      },
      {
        field: 'INVESTIDOR_DESTINO',
        headerName: 'Investidor Destino',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const cpfCnpj = (params.row as TransferenciaResumoResponse).investidorDestinoCpfCnpj;
          return cpfCnpj ? addCpfCnpjMask(cpfCnpj) : '';
        },
        width: 180,
        minWidth: 150,
        maxWidth: 250,
      },
      {
        field: 'QUANTIDADE',
        headerName: 'Quantidade',
        type: 'number',
        filterable: false,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          formatNumber((params.row as TransferenciaResumoResponse).quantidade, { minimumFractionDigits: 0, maximumFractionDigits: 8 }),
        width: 130,
        minWidth: 110,
        maxWidth: 200,
      },
      {
        field: 'HAS_DOCUMENTS',
        headerName: 'Documento',
        type: 'singleSelect',
        valueOptions: [
          { value: 'true', label: 'Possui documento' },
          { value: 'false', label: 'Sem documento' },
        ],
        filterOperators: getGridSingleSelectOperators().filter(({ value }) => ['is'].includes(value)),
        filterable: true,
        sortable: true,
        disableExport: true,
        width: 110,
        minWidth: 100,
        maxWidth: 150,
        valueGetter: (params: GridValueGetterParams): string => {
          const row = params.row as TransferenciaResumoResponse;
          return row.hasDocuments ? 'true' : 'false';
        },
        renderCell: (params: GridCellParams) => {
          const row = params.row as TransferenciaResumoResponse;
          return row.hasDocuments ? (
            <Tooltip title="Possui documento">
              <span style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <span style={{
                  display: 'inline-block',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                }} />
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Sem documento">
              <span style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <span style={{
                  display: 'inline-block',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: '#d1c4e9',
                }} />
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: 'actions',
        headerName: ' ',
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params: GridCellParams) => {
          const operacao = params.row as TransferenciaResumoResponse;
          const isPendente = operacao.status === TransferenciaStatusEnum.Pendente;
          const podeAprovar = isPendente && (isDepositaria || participanteLogado.cnpj === operacao.custodianteDestinoCnpj);
          return (
            <MoreOptionsMenu>
              <MenuItem
                onClick={() => history.push(`/operacoes/gerenciar/transferencias/${operacao.id}`)}
              >
                <Description fontSize="small" style={{ marginRight: '10px' }} />
                {'Ver Detalhes'}
              </MenuItem>
              <MenuItem onClick={() => handleOpenGestaoComprovantes(operacao)}>
                <AttachFile fontSize="small" style={{ marginRight: '10px' }} />
                {'Gestão de Documentos'}
              </MenuItem>
              {podeAprovar && (
                <MenuItem onClick={() => handleAprovarIndividual(operacao)}>
                  <ThumbUp fontSize="small" style={{ marginRight: '10px' }} />
                  {'Aprovar'}
                </MenuItem>
              )}
              {isPendente && (
                <MenuItem onClick={() => handleOpenReprovacao(operacao)}>
                  <ThumbDown fontSize="small" style={{ marginRight: '10px' }} />
                  {'Reprovar'}
                </MenuItem>
              )}
            </MoreOptionsMenu>
          );
        },
        width: 75,
        minWidth: 75,
        maxWidth: 200,
      },
    ],
    [classes, filterOperators, filterOperatorsData, filterOperatorsSelect, history]
  );

  return (
    <div>
      <SmSpace />

      <Grid container justifyContent="flex-start" spacing={4}>
        <Grid item style={{ display: 'inline-flex' }}>
          <CheckCircle fontSize="small" style={{ color: '#00c853' }} />
          <Typography display="inline">{'Concluído'}</Typography>
        </Grid>
        <Grid item style={{ display: 'inline-flex' }}>
          <Error fontSize="small" style={{ color: '#2962ff' }} />
          <Typography display="inline">{'Em Análise'}</Typography>
        </Grid>
        <Grid item style={{ display: 'inline-flex' }}>
          <Cancel fontSize="small" style={{ color: '#f44336' }} />
          <Typography display="inline">{'Reprovado'}</Typography>
        </Grid>
        <Grid item style={{ display: 'inline-flex' }}>
          <WatchLater fontSize="small" style={{ color: '#9e9e9e' }} />
          <Typography display="inline">{'Indefinido'}</Typography>
        </Grid>
      </Grid>

      <SmSpace />

      <Box display="flex" justifyContent="flex-end" alignItems="center">
        {selectionModel.length > 0 && operacoesSelecionadasEmAnalise.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAprovacaoEmLote}
            style={{ whiteSpace: 'nowrap' }}
          >
            {`Aprovar em lote (${operacoesSelecionadasEmAnalise.length})`}
          </Button>
        )}
      </Box>

      {selectionModel.length > 0 && operacoesSelecionadasEmAnalise.length > 0 && <MdSpace />}

      <DataGridPro
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={componentsPropsValues}
        getRowId={(row) => (row as TransferenciaResumoResponse).id}
        rows={operacoes}
        columns={getGridColumns()}
        components={{
          Toolbar: CustomToolBarUtils,
        }}
        checkboxSelection
        isRowSelectable={(params) => {
          const row = params.row as TransferenciaResumoResponse;
          const isPendente = row.status === TransferenciaStatusEnum.Pendente;
          const podeAprovar = isDepositaria || participanteLogado.cnpj === row.custodianteDestinoCnpj;
          return isPendente && podeAprovar;
        }}
        disableSelectionOnClick
        selectionModel={selectionModel}
        onSelectionModelChange={(newModel) => setSelectionModel(newModel)}
        rowsPerPageOptions={rowsPerPageOptionsValues}
        page={page}
        rowCount={pageCount}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize), setPage(0);
        }}
        pageSize={pageSize}
        loading={showLoader}
        filterMode="server"
        onFilterModelChange={onFilterChange}
        sortingMode="server"
        onSortModelChange={onSortChange}
      />

      {/* Modal de aprovação em lote */}
      <AprovacaoEmLoteTransferenciasModal
        open={openAprovacaoEmLoteModal}
        operacoes={operacoesSelecionadasEmAnalise}
        onClose={handleCloseAprovacaoEmLote}
        onSuccess={handleAprovacaoEmLoteSuccess}
      />

      {/* Drawer de gestão de documentos */}
      <GestaoDocumentosTransferenciaDrawer
        operacaoId={comprovantesTransferenciaId}
        codigoInstrumento={comprovantesCodigoIF}
        documentos={transferenciaDocumentos}
        opened={openGestaoComprovantesDrawer}
        onClose={handleCloseGestaoComprovantes}
        onUploadSuccess={handleGestaoComprovantesUploadSuccess}
      />

      {/* Dialog de reprovação individual */}
      <Dialog open={openReprovacaoDialog} onClose={handleCloseReprovacao} maxWidth="sm" fullWidth>
        <DialogTitle>{'Reprovar Transferência'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            {'Informe a justificativa para a reprovação:'}
          </Typography>
          <MuiTextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label="Justificativa"
            value={reprovacaoJustificativa}
            onChange={(e) => setReprovacaoJustificativa(e.target.value)}
          />
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button variant="outlined" onClick={handleCloseReprovacao}>
            {'Cancelar'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmarReprovacao}
            disabled={!reprovacaoJustificativa.trim()}
          >
            {'Reprovar'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Transferencias;
